// src/components/modals/EditProductInventoryModal.jsx
import React, { useState, useEffect } from 'react'
import { X, Edit, AlertCircle, Package } from 'lucide-react'
import ProductInventoryService from '../../services/productInventoryService'
import ProductInventoryModel from '../../utils/productInventoryModel'
import AuthService from '../../services/authService'

const EditProductInventoryModal = ({ isOpen, onClose, inventory, onInventoryUpdated }) => {
  const currentUser = AuthService.getCurrentUser()
  const isSuperAdmin =
    currentUser?.role?.toLowerCase() === 'super_admin' ||
    currentUser?.role?.toLowerCase() === 'superadmin'

  const [formData, setFormData] = useState({
    currentStock: ''
  })
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})

  // Reset form when modal opens/closes or inventory changes
  useEffect(() => {
    if (isOpen && inventory) {
      setFormData({
        currentStock: inventory.currentStock?.toString() || '0'
      })
      setErrors({})
    }
  }, [isOpen, inventory])

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  // Validate form
  const validateForm = () => {
    const validation = ProductInventoryModel.validatePartial(formData)
    setErrors(validation.errors)
    return validation.isValid
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) return

    if (!isSuperAdmin) {
      setErrors({ general: 'Only users with the Super Admin role are authorized to edit current stock.' })
      return
    }

    try {
      setLoading(true)

      const newStock = Number(formData.currentStock)
      const originalStock = Number(inventory.currentStock) || 0

      // No changes — just close
      if (newStock === originalStock) {
        onClose()
        return
      }

      const updateData = { currentStock: newStock }

      const response = await ProductInventoryService.updateInventory(inventory._id, updateData)

      if (response.success) {
        // Merge backend data with local — backend response is the source of truth
        const updatedInventory = response.data?.productInventory
          ? response.data.productInventory
          : { ...inventory, currentStock: newStock }

        onInventoryUpdated(updatedInventory)
        setFormData({ currentStock: '' })
        setErrors({})
        onClose()
      } else {
        setErrors({ general: response.message || 'Failed to update inventory' })
      }
    } catch (error) {
      console.error('❌ Error updating inventory:', error)
      let errorMessage = 'Failed to update inventory. Please try again.'
      if (error.message?.includes('Not found')) {
        errorMessage = 'Inventory not found. It may have been deleted.'
      }
      setErrors({ general: errorMessage })
    } finally {
      setLoading(false)
    }
  }

  // Handle modal close
  const handleClose = () => {
    if (!loading) {
      setFormData({ currentStock: '' })
      setErrors({})
      onClose()
    }
  }

  if (!isOpen || !inventory) return null

  const currentStock = Number(inventory.currentStock) || 0
  const threshold = Number(inventory.product?.threshold) || 10
  const status = ProductInventoryService.getStockStatus(currentStock, threshold)
  const statusColor = ProductInventoryModel.getStatusColor(status)
  const statusLabel = ProductInventoryModel.getStatusLabel(status)

  const newStock = Number(formData.currentStock)
  const newStatus = ProductInventoryService.getStockStatus(newStock, threshold)

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
          onClick={handleClose}
        />

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        {/* Modal Panel */}
        <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
          <form onSubmit={handleSubmit}>

            {/* Header */}
            <div className="px-6 pt-6 pb-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center h-10 w-10 rounded-full bg-orange-100">
                    <Edit className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <h3 className="text-lg font-semibold text-gray-900" id="modal-title">
                        Edit Inventory
                      </h3>
                      {isSuperAdmin && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-purple-100 text-purple-700">
                          Super Admin
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {isSuperAdmin ? 'Direct stock adjustment override' : 'Update current stock level'}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={loading}
                  className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-5">

              {/* Product Info Card */}
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-white border border-gray-200">
                      <Package className="h-5 w-5 text-gray-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {inventory.product?.name || 'Unknown Product'}
                      </p>
                      <p className="text-xs text-gray-500">
                        SKU: {inventory.product?.sku || 'N/A'} · {inventory.branch?.name || 'Unknown Branch'}
                      </p>
                    </div>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColor}`}>
                    {statusLabel}
                  </span>
                </div>
              </div>

              {/* Error Alert */}
              {errors.general && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-3 flex items-center space-x-2">
                  <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0" />
                  <p className="text-sm text-red-700">{errors.general}</p>
                </div>
              )}

              {/* Current Stock Field */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label htmlFor="currentStock" className="block text-sm font-medium text-gray-700">
                    Current Stock <span className="text-red-500">*</span>
                  </label>
                  {isSuperAdmin ? (
                    <span className="text-xs font-medium text-purple-600 bg-purple-50 px-2 py-0.5 rounded">
                      Super Admin Editable
                    </span>
                  ) : (
                    <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded">
                      Super Admin Only
                    </span>
                  )}
                </div>
                <div className="relative">
                  <input
                    type="number"
                    id="currentStock"
                    name="currentStock"
                    value={formData.currentStock}
                    onChange={handleInputChange}
                    disabled={loading || !isSuperAdmin}
                    min="0"
                    step="any"
                    className={`block w-full px-4 py-2.5 pr-16 border rounded-lg text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed ${
                      errors.currentStock ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="Enter stock quantity"
                  />
                  <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                    <span className="text-sm font-medium text-gray-500">
                      {inventory.product?.unit?.abbreviation || 'units'}
                    </span>
                  </div>
                </div>
                {errors.currentStock && (
                  <p className="mt-1.5 text-xs text-red-600 flex items-center space-x-1">
                    <AlertCircle className="h-3 w-3 inline" /> {errors.currentStock}
                  </p>
                )}
                {!isSuperAdmin && (
                  <p className="mt-1.5 text-xs text-amber-600">
                    Only users with the Super Admin role can edit current stock.
                  </p>
                )}
                <p className="mt-1.5 text-xs text-gray-500">
                  Current: <strong>{currentStock}</strong> · Threshold: <strong>{threshold}</strong> {inventory.product?.unit?.abbreviation || 'units'}
                </p>
              </div>

              {/* Live Preview */}
              {formData.currentStock !== '' && newStock !== currentStock && (
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 flex items-center justify-between">
                  <span className="text-sm text-blue-800">
                    New level: <strong>{newStock.toLocaleString()}</strong> {inventory.product?.unit?.abbreviation || 'units'}
                  </span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    ProductInventoryModel.getStatusColor(newStatus)
                  }`}>
                    {ProductInventoryModel.getStatusLabel(newStatus)}
                  </span>
                </div>
              )}

              {formData.currentStock !== '' && newStock === currentStock && (
                <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-3">
                  <p className="text-sm text-yellow-700">No change — stock value is the same as current.</p>
                </div>
              )}

            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="w-full sm:w-auto inline-flex justify-center items-center px-5 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="w-full sm:w-auto inline-flex justify-center items-center px-5 py-2.5 border border-transparent rounded-lg text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Updating...
                  </>
                ) : (
                  <>
                    <Edit className="h-4 w-4 mr-2" />
                    Update Stock
                  </>
                )}
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  )
}

export default EditProductInventoryModal